import {
  Box,
  Button,
  Card,
  Center,
  PasswordInput,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { InlineError } from "../components/ui";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { ApiClientError, api } from "../lib/api";

/**
 * Two ways in.
 *
 *  - Mobile + OTP for field staff (REQ-AUTH-1). No password to remember or
 *    share, which is the right trade for people working from a phone.
 *  - Username + password for the Store Manager and Admin, who work from a
 *    desk and should not depend on an SMS arriving.
 *
 * The OTP tab is the default because it serves most of the roles. Which method
 * a given account may use is decided by the server - this only chooses a form.
 */
type Method = "otp" | "password";

export function Login() {
  const [method, setMethod] = useState<Method>("otp");

  return (
    <Center mih="100dvh" p="lg" bg="gray.0">
      <Card withBorder radius="md" padding="xl" w="100%" maw={420}>
        <Stack gap="md">
          <Stack gap={0} align="center">
            <Title order={3}>Supply X</Title>
            <Text fz="md" c="gray.6">
              Harsha Agencies
            </Text>
          </Stack>

          <Tabs
            value={method}
            onChange={(value) => setMethod((value as "otp" | "password") ?? "otp")}
          >
            <Tabs.List grow aria-label="Sign-in method">
              <Tabs.Tab value="otp">Mobile code</Tabs.Tab>
              <Tabs.Tab value="password">Username</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {method === "otp" ? <OtpForm /> : <PasswordForm />}

          <Text fz="sm" c="gray.6" ta="center">
            {method === "otp"
              ? "For field staff. A code is sent to your registered mobile number."
              : "For the Store Manager and Admin."}
          </Text>
        </Stack>
      </Card>
    </Center>
  );
}

// -------------------------------------------------------------------------

function OtpForm() {
  const { signIn } = useAuth();
  const [step, setStep] = useState<"mobile" | "code">("mobile");
  const [mobileNumber, setMobileNumber] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [secondsLeft, setSecondsLeft] = useState(0);
  const codeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  async function submitMobile(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const result = await api.requestOtp(mobileNumber);
      setDevCode(result.devCode);
      setSecondsLeft(result.expiresInSeconds);
      setStep("code");
    } catch (caught) {
      setError(
        caught instanceof ApiClientError ? caught.message : "Could not send a code.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const { token } = await api.verifyOtp(mobileNumber, code);
      await signIn(token);
    } catch (caught) {
      setError(
        caught instanceof ApiClientError ? caught.message : "Could not verify that code.",
      );
      setCode("");
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  if (step === "mobile") {
    return (
      <form onSubmit={submitMobile}>
        <Stack gap="sm">
          <TextInput
            id="mobile"
            label="Mobile number"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            enterKeyHint="go"
            placeholder="10-digit mobile number"
            className="num"
            value={mobileNumber}
            onChange={(event) => setMobileNumber(event.currentTarget.value)}
            required
            autoFocus
          />
          {error && <InlineError>{error}</InlineError>}
          <Button
            type="submit"
            size="md"
            fullWidth
            loading={busy}
            disabled={mobileNumber.trim().length < 10}
          >
            Send code
          </Button>
        </Stack>
      </form>
    );
  }

  return (
    <form onSubmit={submitCode}>
      <Stack gap="sm">
        <TextInput
          ref={codeRef}
          id="code"
          label="Verification code"
          inputMode="numeric"
          autoComplete="one-time-code"
          enterKeyHint="go"
          placeholder="6-digit code"
          className="num"
          value={code}
          onChange={(event) =>
            setCode(event.currentTarget.value.replace(/\D/g, "").slice(0, 8))
          }
          required
        />

        {devCode && (
          // Only ever present when SMS_PROVIDER=dev.
          //
          // An explicitly LABELLED band, not a soft yellow alert. The brief is
          // right about why: a grey-yellow notice reads like ordinary product
          // copy, and a code printed on the sign-in screen that looks like
          // ordinary product copy is one somebody eventually trusts in
          // production. This looks like scaffolding because it is scaffolding
          // - it goes the moment DLT approval lands and real SMS is sent.
          <Box
            style={{
              border: "2px dashed #fa5252",
              borderRadius: "var(--mantine-radius-md)",
              background: "#fff5f5",
              padding: 12,
            }}
          >
            <Text
              fz={11}
              fw={700}
              c="red.8"
              tt="uppercase"
              style={{ letterSpacing: ".08em" }}
            >
              Development mode — not a real message
            </Text>
            <Text fz="sm" c="gray.8" mt={4}>
              No SMS was sent. The code is{" "}
              <strong className="num">{devCode}</strong>.
            </Text>
            <Text fz={12} c="gray.6" mt={2}>
              This band disappears once messages are really being delivered.
            </Text>
          </Box>
        )}

        <Text fz="sm" c="gray.6">
          Sent to <span className="num">{mobileNumber}</span>
          {secondsLeft > 0
            ? ` · expires in ${Math.floor(secondsLeft / 60)}:${String(
                secondsLeft % 60,
              ).padStart(2, "0")}`
            : " · expired"}
        </Text>

        {error && <InlineError>{error}</InlineError>}

        <Button type="submit" size="md" fullWidth loading={busy} disabled={code.length < 4}>
          Sign in
        </Button>
        <Button
          variant="subtle"
          color="gray"
          onClick={() => {
            setStep("mobile");
            setCode("");
            setError(undefined);
          }}
        >
          Use a different number
        </Button>
      </Stack>
    </form>
  );
}

// -------------------------------------------------------------------------

function PasswordForm() {
  const { signIn } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const { token } = await api.login(username, password);
      // signIn re-reads /auth/me, which reports whether a password change is
      // outstanding; App routes to the change screen from there.
      await signIn(token);
    } catch (caught) {
      setError(
        caught instanceof ApiClientError ? caught.message : "Could not sign in.",
      );
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="sm">
        <TextInput
          id="username"
          label="Username"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={username}
          onChange={(event) => setUsername(event.currentTarget.value)}
          required
          autoFocus
        />

        {/* PasswordInput carries its own show/hide, so the separate button
            is gone - one control, and the one people already know. */}
        <PasswordInput
          id="password"
          label="Password"
          autoComplete="current-password"
          enterKeyHint="go"
          value={password}
          onChange={(event) => setPassword(event.currentTarget.value)}
          required
        />

        {error && <InlineError>{error}</InlineError>}

        <Button
          type="submit"
          size="md"
          fullWidth
          loading={busy}
          disabled={username.trim().length < 3 || password.length < 1}
        >
          Sign in
        </Button>
      </Stack>
    </form>
  );
}
